import type { ComponentBaseMapper, EventStateRegistry, TriggerRenderer } from "../types";
import { onPushTriggerRenderer } from "./on_push";
import { onPullRequestTriggerRenderer } from "./on_pull_request";
import { onIssueTriggerRenderer } from "./on_issue";
import { onIssueCommentTriggerRenderer } from "./on_issue_comment";
import { onReleaseTriggerRenderer } from "./on_release";
import { onTagCreatedTriggerRenderer } from "./on_tag_created";
import { onBranchCreatedTriggerRenderer } from "./on_branch_created";
import { onPRCommentTriggerRenderer } from "./on_pr_comment";
import { onPRReviewTriggerRenderer } from "./on_pr_review";
import { onPRReviewCommentTriggerRenderer } from "./on_pr_review_comment";
import { onWorkflowRunTriggerRenderer } from "./on_workflow_run";
import { onCommitStatusTriggerRenderer } from "./on_commit_status";
import { onCheckRunTriggerRenderer } from "./on_check_run";
import { baseIssueMapper } from "./base";
import { RUN_WORKFLOW_STATE_REGISTRY, runWorkflowMapper } from "./run_workflow";
import {
  WAIT_FOR_PULL_REQUEST_CHECKS_STATE_REGISTRY,
  waitForPullRequestChecksMapper,
} from "./wait_for_pull_request_checks";
import { publishCommitStatusMapper } from "./publish_commit_status";
import { createDeploymentMapper } from "./create_deployment";
import { createDeploymentStatusMapper } from "./create_deployment_status";
import { createIssueCommentMapper } from "./create_issue_comment";
import { updateIssueCommentMapper } from "./update_issue_comment";
import { createReleaseMapper } from "./create_release";
import { updateReleaseMapper } from "./update_release";
import { deleteReleaseMapper } from "./delete_release";
import { getReleaseMapper } from "./get_release";
import { getRepositoryPermissionMapper } from "./get_repository_permission";
import { createReviewMapper } from "./create_review";
import { createPullRequestMapper } from "./create_pull_request";
import { mergePullRequestMapper } from "./merge_pull_request";
import { markPullRequestReadyForReviewMapper } from "./mark_pull_request_ready_for_review";
import { addPullRequestReviewersMapper } from "./add_pull_request_reviewers";
import { updatePullRequestMapper } from "./update_pull_request";
import { FIND_PULL_REQUEST_STATE_REGISTRY, findPullRequestMapper } from "./find_pull_request";
import { getWorkflowUsageMapper } from "./get_workflow_usage";
import { labelsMapper } from "./labels";
import { addReactionMapper } from "./add_reaction";
import { getCombinedCommitStatusMapper } from "./get_combined_commit_status";
import { listCheckRunsForRefMapper } from "./list_check_runs_for_ref";
import { buildActionStateRegistry } from "../utils";

export const eventStateRegistry: Record<string, EventStateRegistry> = {
  runWorkflow: RUN_WORKFLOW_STATE_REGISTRY,
  waitForPullRequestChecks: WAIT_FOR_PULL_REQUEST_CHECKS_STATE_REGISTRY,
  createIssue: buildActionStateRegistry("created"),
  createIssueComment: buildActionStateRegistry("created"),
  updateIssueComment: buildActionStateRegistry("updated"),
  getIssue: buildActionStateRegistry("retrieved"),
  updateIssue: buildActionStateRegistry("updated"),
  createReview: buildActionStateRegistry("created"),
  createPullRequest: buildActionStateRegistry("created"),
  mergePullRequest: buildActionStateRegistry("merged"),
  markPullRequestReadyForReview: buildActionStateRegistry("marked ready"),
  addPullRequestReviewers: buildActionStateRegistry("added"),
  updatePullRequest: buildActionStateRegistry("updated"),
  findPullRequest: FIND_PULL_REQUEST_STATE_REGISTRY,
  publishCommitStatus: buildActionStateRegistry("published"),
  createDeployment: buildActionStateRegistry("created"),
  createDeploymentStatus: buildActionStateRegistry("created"),
  createRelease: buildActionStateRegistry("created"),
  updateRelease: buildActionStateRegistry("updated"),
  deleteRelease: buildActionStateRegistry("deleted"),
  getRelease: buildActionStateRegistry("retrieved"),
  getRepositoryPermission: buildActionStateRegistry("retrieved"),
  getWorkflowUsage: buildActionStateRegistry("retrieved"),
  getCombinedCommitStatus: buildActionStateRegistry("retrieved"),
  listCheckRunsForRef: buildActionStateRegistry("retrieved"),
  addIssueLabel: buildActionStateRegistry("added"),
  removeIssueLabel: buildActionStateRegistry("removed"),
  addIssueAssignee: buildActionStateRegistry("added"),
  removeIssueAssignee: buildActionStateRegistry("removed"),
  addReaction: buildActionStateRegistry("added"),
};

export const componentMappers: Record<string, ComponentBaseMapper> = {
  createIssue: baseIssueMapper,
  createIssueComment: createIssueCommentMapper,
  updateIssueComment: updateIssueCommentMapper,
  getIssue: baseIssueMapper,
  updateIssue: baseIssueMapper,
  createReview: createReviewMapper,
  createPullRequest: createPullRequestMapper,
  mergePullRequest: mergePullRequestMapper,
  markPullRequestReadyForReview: markPullRequestReadyForReviewMapper,
  addPullRequestReviewers: addPullRequestReviewersMapper,
  updatePullRequest: updatePullRequestMapper,
  findPullRequest: findPullRequestMapper,
  runWorkflow: runWorkflowMapper,
  waitForPullRequestChecks: waitForPullRequestChecksMapper,
  publishCommitStatus: publishCommitStatusMapper,
  createDeployment: createDeploymentMapper,
  createDeploymentStatus: createDeploymentStatusMapper,
  createRelease: createReleaseMapper,
  updateRelease: updateReleaseMapper,
  deleteRelease: deleteReleaseMapper,
  getRelease: getReleaseMapper,
  getRepositoryPermission: getRepositoryPermissionMapper,
  getWorkflowUsage: getWorkflowUsageMapper,
  getCombinedCommitStatus: getCombinedCommitStatusMapper,
  listCheckRunsForRef: listCheckRunsForRefMapper,
  addIssueLabel: labelsMapper,
  removeIssueLabel: labelsMapper,
  addIssueAssignee: baseIssueMapper,
  removeIssueAssignee: baseIssueMapper,
  addReaction: addReactionMapper,
};

export const triggerRenderers: Record<string, TriggerRenderer> = {
  onPush: onPushTriggerRenderer,
  onPullRequest: onPullRequestTriggerRenderer,
  onPRComment: onPRCommentTriggerRenderer,
  onPRReview: onPRReviewTriggerRenderer,
  onPRReviewComment: onPRReviewCommentTriggerRenderer,
  onIssue: onIssueTriggerRenderer,
  onIssueComment: onIssueCommentTriggerRenderer,
  onRelease: onReleaseTriggerRenderer,
  onTagCreated: onTagCreatedTriggerRenderer,
  onBranchCreated: onBranchCreatedTriggerRenderer,
  onWorkflowRun: onWorkflowRunTriggerRenderer,
  onCommitStatus: onCommitStatusTriggerRenderer,
  onCheckRun: onCheckRunTriggerRenderer,
};
